import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { Users, Wallet, Clock, ArrowUpRight, Play, BarChart3, Plus } from 'lucide-react';
import { Card } from '../components/ui/card';
import { StatusBadge } from '../components/common/StatusBadge';
import { PageHeader } from '../components/common/PageHeader';
import { selectUser } from '../store/authSlice';
import { reportService } from '../services/index';
import { KEYS } from '../lib/queryKeys';
import { formatINR } from '../lib/format';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';

const StatCard = ({ label, value, delta, icon: Icon, accent, testid }) => (
  <Card className="p-5 sm:p-6 border-slate-200 shadow-sm" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">{label}</div>
        <div className={`mt-3 text-3xl font-semibold tracking-tight font-mono ${accent ? 'text-[#2E6DA4]' : 'text-slate-900'}`}>
          {value}
        </div>
        {delta && (
          <div className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-600 font-medium">
            <ArrowUpRight className="w-3.5 h-3.5" /> {delta}
          </div>
        )}
      </div>
      <div className={`w-10 h-10 rounded-md flex items-center justify-center ${accent ? 'bg-[#2E6DA4]/10 text-[#2E6DA4]' : 'bg-slate-100 text-slate-600'}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const user     = useSelector(selectUser);

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: KEYS.dashboard(),
    queryFn:  reportService.dashboard,
  });

  const { data: trendData, isLoading: trendLoading } = useQuery({
    queryKey: KEYS.trend(6),
    queryFn:  () => reportService.trend(6),
  });

  const ds    = stats?.data  || {};
  const trend = (trendData?.data || []).map((r) => ({
    month:  `${r.month}/${String(r.year).slice(2)}`,
    amount: r.totalNetPayroll,
  }));

  const firstName = user?.ownerName?.split(' ')[0] || 'there';

  return (
    <div>
      <PageHeader
        title={`Welcome back, ${firstName}.`}
        description="Here's what's happening with your payroll this month."
      >
        <button
          onClick={() => navigate('/payroll')}
          className="inline-flex items-center gap-2 h-9 px-4 bg-[#2E6DA4] hover:bg-[#255a88] text-white text-sm font-medium rounded-md transition-colors"
        >
          <Play className="w-4 h-4" /> Run Payroll
        </button>
      </PageHeader>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6">
        <StatCard
          label="Total Employees"
          value={statsLoading ? '…' : (ds.totalEmployees || 0)}
          icon={Users}
          testid="kpi-total-employees"
        />
        <StatCard
          label="This Month Payroll"
          value={statsLoading ? '…' : formatINR(ds.currentMonth?.totalPayroll || 0)}
          icon={Wallet}
          accent
          testid="kpi-month-payroll"
        />
        <StatCard
          label="Payroll Status"
          value={statsLoading ? '…' : (ds.currentMonth?.status || 'not started')}
          icon={Clock}
          testid="kpi-payroll-status"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Trend chart */}
        <Card className="col-span-1 lg:col-span-2 p-5 sm:p-6 border-slate-200 shadow-sm">
          <div className="flex items-start justify-between mb-5">
            <div>
              <h3 className="text-base font-semibold text-slate-900 font-heading">Monthly payroll expense</h3>
              <p className="text-xs text-slate-500 mt-0.5">Trailing 6 months</p>
            </div>
          </div>
          <div className="h-64">
            {trendLoading ? (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">Loading…</div>
            ) : trend.length === 0 ? (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">Run your first payroll to see trends</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trend} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="month" stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="#94A3B8" fontSize={12} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} tickLine={false} axisLine={false} />
                  <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13 }} cursor={{ fill: '#F1F5F9' }} />
                  <Bar dataKey="amount" radius={[6, 6, 0, 0]}>
                    {trend.map((_, i) => <Cell key={i} fill={i === trend.length - 1 ? '#2E6DA4' : '#93C5FD'} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        {/* Quick actions */}
        <Card className="p-5 sm:p-6 border-slate-200 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 font-heading mb-4">Quick actions</h3>
          <div className="space-y-2.5">
            {[
              { label: 'Run Payroll', sub: 'Start monthly run', icon: Play,     color: 'bg-[#2E6DA4]/10 text-[#2E6DA4]', to: '/payroll'    },
              { label: 'Add Employee', sub: 'New onboarding',  icon: Plus,     color: 'bg-emerald-50 text-emerald-600',    to: '/employees'  },
              { label: 'View Reports', sub: 'Analytics',       icon: BarChart3, color: 'bg-amber-50 text-amber-600',        to: '/reports'    },
            ].map((a) => {
              const Icon = a.icon;
              return (
                <button key={a.label} onClick={() => navigate(a.to)}
                  className="w-full flex items-center justify-between p-3 rounded-md border border-slate-200 hover:border-[#2E6DA4] hover:bg-slate-50 transition-colors group">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-md flex items-center justify-center ${a.color}`}><Icon className="w-4 h-4" /></div>
                    <div className="text-left">
                      <div className="text-sm font-medium text-slate-900">{a.label}</div>
                      <div className="text-xs text-slate-500">{a.sub}</div>
                    </div>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-[#2E6DA4] transition-colors" />
                </button>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Recent runs */}
      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between p-5 sm:p-6 border-b border-slate-200">
          <h3 className="text-base font-semibold text-slate-900 font-heading">Recent payroll runs</h3>
          <button onClick={() => navigate('/payroll')} className="text-sm text-[#2E6DA4] hover:underline">View all</button>
        </div>
        {statsLoading ? (
          <div className="p-8 text-center text-slate-400 text-sm">Loading…</div>
        ) : (ds.recentRuns || []).length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">No payroll runs yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Month</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(ds.recentRuns || []).map((run) => (
                  <tr key={run._id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 text-sm font-medium text-slate-900">{run.month}/{run.year}</td>
                    <td className="px-6 py-4 text-sm text-right font-mono font-medium text-slate-900">{formatINR(run.totalNetPayroll)}</td>
                    <td className="px-6 py-4"><StatusBadge status={run.status?.toUpperCase()} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}